import { SIMAElementClassifier, SIMARouteType, } from "../types";
import { SurveyPointIndex } from "./survey-point-index";
/**
 * SIMA 路線 (F00..F99 ブロック) を表すモデル。
 * キャメルケースの直接フィールド (routeNumber, groupNumber, routeName, routeType) を持ち、
 * 測量座標点解決には SurveyPointIndex を利用します。
 */
export class Route {
    routeNumber;
    groupNumber;
    routeName;
    routeType;
    routeElements;
    routeAttributes;
    _linkedCoords = new Map();
    _surveyPointIndex;
    constructor(routeData, surveyPointsOrIndex) {
        if (routeData) {
            const begin = routeData.routeAttributes.find((attr) => attr.classifier === SIMAElementClassifier.ROUTE_BEGIN);
            this.routeNumber = begin?.route_number ?? 1;
            this.groupNumber = begin?.group_number ?? 1;
            this.routeName = begin?.route_name ?? "";
            this.routeType = begin?.route_type ?? SIMARouteType.MAIN_LINE;
            const extraAttrs = routeData.routeAttributes.filter((attr) => attr.classifier === SIMAElementClassifier.ROUTE_ATTRIBUTES);
            if (extraAttrs.length > 0) {
                this.routeAttributes = extraAttrs;
            }
            this.routeElements = [...routeData.routeElements];
        }
        else {
            this.routeNumber = 1;
            this.groupNumber = 1;
            this.routeName = "";
            this.routeType = SIMARouteType.MAIN_LINE;
            this.routeElements = [];
        }
        if (surveyPointsOrIndex) {
            this.resolveSurveyPoints(surveyPointsOrIndex);
        }
    }
    /**
     * B01 路線座標要素によって参照されている測量座標点一覧を取得します。
     */
    get surveyPoints() {
        const points = [];
        const seen = new Set();
        for (const element of this.routeElements) {
            if (element.classifier === SIMAElementClassifier.ROUTE_COORD) {
                const routeCoord = element;
                const matched = this._linkedCoords.get(routeCoord);
                if (matched && !seen.has(matched)) {
                    seen.add(matched);
                    points.push(matched);
                }
            }
        }
        return points;
    }
    /**
     * SurveyPointIndex または SurveyPoint 配列を用いて路線座標 (B01) を解決します。
     */
    resolveSurveyPoints(indexOrPoints) {
        const index = Array.isArray(indexOrPoints)
            ? new SurveyPointIndex(indexOrPoints)
            : indexOrPoints;
        this._surveyPointIndex = index;
        this._linkedCoords.clear();
        for (const element of this.routeElements) {
            if (element.classifier === SIMAElementClassifier.ROUTE_COORD) {
                const routeCoord = element;
                const matched = index.lookup(routeCoord.point_number, routeCoord.point_name);
                if (matched) {
                    this._linkedCoords.set(routeCoord, matched);
                }
            }
        }
    }
    /**
     * 参照している SurveyPoint の更新に合わせて B01 路線要素の点番号・点名を同期します。
     */
    syncRouteCoords() {
        for (const [routeCoord, sp] of this._linkedCoords) {
            routeCoord.point_number = sp.pointNumber;
            routeCoord.point_name = sp.pointName;
        }
    }
    /**
     * 測量座標点を B01 路線座標として路線に追加します。
     */
    addSurveyPoint(surveyPoint) {
        if (this._surveyPointIndex) {
            this._surveyPointIndex.add(surveyPoint);
        }
        else {
            this._surveyPointIndex = new SurveyPointIndex([surveyPoint]);
        }
        const routeCoord = {
            classifier: SIMAElementClassifier.ROUTE_COORD,
            point_number: surveyPoint.pointNumber,
            point_name: surveyPoint.pointName,
        };
        this.routeElements.push(routeCoord);
        this._linkedCoords.set(routeCoord, surveyPoint);
    }
    /**
     * ワイヤフォーマット (ISIMARoute 構造) に変換して返却します。
     */
    toRouteData() {
        this.syncRouteCoords();
        const begin = {
            classifier: SIMAElementClassifier.ROUTE_BEGIN,
            route_number: this.routeNumber,
            group_number: this.groupNumber,
            route_name: this.routeName,
            route_type: this.routeType,
        };
        const routeAttributes = [begin];
        if (this.routeAttributes) {
            routeAttributes.push(...this.routeAttributes);
        }
        return {
            routeAttributes,
            routeElements: this.routeElements,
        };
    }
}
//# sourceMappingURL=route.js.map